import { useState, useEffect, useMemo } from "react";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const PRESETS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "qtd", label: "Quarter to date" },
  { key: "ytd", label: "Year to date" },
  { key: "custom", label: "Custom range" },
];

function isoDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function fromIso(s) {
  return s ? new Date(`${s}T00:00:00`) : undefined;
}

/**
 * DateRangePicker — controlled component.
 * Props:
 *   value: { preset, from, to }  where preset ∈ PRESETS.key ; from/to are ISO date strings.
 *   onChange(next)
 */
export default function DateRangePicker({ value, onChange, testId = "date-range" }) {
  const { preset = "30d", from = null, to = null } = value || {};
  const [customFrom, setCustomFrom] = useState(from);
  const [customTo, setCustomTo] = useState(to);

  useEffect(() => { setCustomFrom(from); setCustomTo(to); }, [from, to]);

  const activeLabel = useMemo(() => {
    const p = PRESETS.find((x) => x.key === preset);
    if (preset === "custom") return from && to ? `${from} → ${to}` : "Custom range";
    return p?.label || "Range";
  }, [preset, from, to]);

  const pickPreset = (key) => {
    if (key === "custom") {
      onChange({ preset: "custom", from: customFrom || null, to: customTo || null });
    } else {
      onChange({ preset: key, from: null, to: null });
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid={`${testId}-preset-trigger`}>
            <CalendarIcon className="h-3.5 w-3.5 mr-1" /> {activeLabel}
            <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {PRESETS.map((p) => (
            <DropdownMenuItem
              key={p.key}
              onClick={() => pickPreset(p.key)}
              data-testid={`${testId}-preset-${p.key}`}
              className={preset === p.key ? "bg-muted" : ""}
            >
              {p.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {preset === "custom" && (
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs", !customFrom && "text-muted-foreground")} data-testid={`${testId}-from`}>
                {customFrom || "From"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={fromIso(customFrom)} onSelect={(d) => {
                const s = isoDate(d);
                setCustomFrom(s);
                onChange({ preset: "custom", from: s, to: customTo });
              }} initialFocus />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs", !customTo && "text-muted-foreground")} data-testid={`${testId}-to`}>
                {customTo || "To"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={fromIso(customTo)} onSelect={(d) => {
                const s = isoDate(d);
                setCustomTo(s);
                onChange({ preset: "custom", from: customFrom, to: s });
              }} initialFocus />
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
}

export function rangeToParams({ preset, from, to }) {
  const params = {};
  if (preset && preset !== "custom") params.preset = preset;
  if (from) params.date_from = from;
  if (to) params.date_to = to;
  return params;
}
